import React, { useContext, useState, useEffect, useRef } from "react";
import { AppContext } from "../../../../../appContext";
import { sendRequest, deleteReport } from "../../../../../api/api";
import { ReportDetailsModal } from "./ReportDetailsModal";
import "./SideMenuAdminReports.css";

interface IReport {
    id: string;
    userId: string;
    userName?: string;
    userFirstName?: string;
    userLastName?: string;
    timestamp: string;
    screenshotUrl?: string;
    message: string;
    logs?: Array<{
        type: string;
        message: string;
        timestamp: string;
    }>;
}

export const SideMenuAdminReports = ({ onClose }: { onClose: () => void }) => {
    const { translate } = useContext(AppContext);
    const [reports, setReports] = useState<IReport[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedReport, setSelectedReport] = useState<IReport | null>(null);
    const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        try {
            setIsLoading(true);
            const response = await sendRequest('getAllReports', 'GET', null, '');
            if (response && response.ok) {
                const data = await response.json();
                setReports(data);
            }
        } catch (error) {
            console.error('Error loading reports:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (timestamp: string) => {
        return new Date(timestamp).toLocaleString();
    };

    const formatUserName = (report: IReport) => {
        if (report.userFirstName && report.userLastName) {
            return `${report.userFirstName} ${report.userLastName}`;
        }
        if (report.userName) {
            return report.userName;
        }
        return `User ID: ${report.userId}`;
    };

    const handleOpenReport = (report: IReport) => {
        setSelectedReport(report);
    };

    const handleCloseModal = () => {
        setSelectedReport(null);
    };

    const getReportId = (report: IReport) => {
        return report.id;
    };

    const handleDeleteReport = async (report: IReport) => {
        const reportId = getReportId(report);
        if (!reportId) {
            setNotification({ message: translate('report.delete_error'), type: 'error' });
            return;
        }

        try {
            const response = await deleteReport(reportId);
            if (response && response.ok) {
                setNotification({ message: translate('report.deleted'), type: 'success' });
                // Обновляем список репортов
                await loadReports();
            } else {
                setNotification({ message: translate('report.delete_error'), type: 'error' });
            }
        } catch (error) {
            console.error('Error deleting report:', error);
            setNotification({ message: translate('report.delete_error'), type: 'error' });
        }

        // Автоматическое скрытие через 5 секунд
        if (notificationTimeoutRef.current) {
            clearTimeout(notificationTimeoutRef.current);
        }
        notificationTimeoutRef.current = setTimeout(() => {
            setNotification(null);
        }, 5000);
    };

    const hideNotification = () => {
        setNotification(null);
    };

    if (isLoading) {
        return (
            <div className="SideMenuAdminReports">
                <div className="SideMenuAdminReports_loading">
                    {translate('admin_loading_reports')}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="SideMenuAdminReports">
                <div className="SideMenuAdminReports_header">
                    <h3>{translate('admin_reports_count')} ({reports.length})</h3>
                </div>
                <div className="SideMenuAdminReports_list">
                    {reports.length === 0 ? (
                        <div className="SideMenuAdminReports_no_reports">
                            {translate('admin_no_reports')}
                        </div>
                    ) : (
                        reports.map((report, index) => (
                            <div key={index} className="SideMenuAdminReports_item">
                                <div className="SideMenuAdminReports_item_info">
                                    <div className="SideMenuAdminReports_item_date">
                                        {formatDate(report.timestamp)}
                                    </div>
                                    <div className="SideMenuAdminReports_item_user">
                                        {formatUserName(report)}
                                    </div>
                                    <div className="SideMenuAdminReports_item_message">
                                        {report.message}
                                    </div>
                                </div>
                                <div className="SideMenuAdminReports_item_buttons">
                                    <button 
                                        className="SideMenuAdminReports_button SideMenuAdminReports_button_open"
                                        onClick={() => handleOpenReport(report)}
                                    >
                                        {translate('admin_report_open')}
                                    </button>
                                    <button 
                                        className="SideMenuAdminReports_button SideMenuAdminReports_button_delete"
                                        onClick={() => handleDeleteReport(report)}
                                    >
                                        {translate('admin_report_delete')}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {selectedReport && (
                <ReportDetailsModal 
                    report={selectedReport} 
                    onClose={handleCloseModal} 
                />
            )}

            {notification && (
                <div className={`notification ${notification.type}`}>
                    <span>{notification.message}</span>
                    <button onClick={hideNotification} className="notification-close">×</button>
                </div>
            )}
        </>
    );
};